import React, { memo } from "react";
import { breakpoints, Paragraph, useMediaQuery } from "@tvg/design-system";
import { Tag } from "@tvg/design-system/web";
import { useFavoriteWithRedux } from "@urp/lib-racetracks";
import { isAdvancedSpecialPick } from "@urp/lib-racetracks/src/utils/trackInfo";
import { buildQaLabel } from "@tvg/test-utils/utils";
import {
  getSpecialPickLabel,
  getSpecialWagerTooltip
} from "@urp/special-wagers/src/utils";
import { get } from "lodash";
import { SpecialWagerTypesNamesEnum } from "@tvg/ts-types/SpecialWager";
import { SpecialWagerTrack } from "@urp/special-wagers/src/types";
import { getSpecialPickTrackName } from "@tvg/design-system/web/components/RaceCell/utils";
import { useSelector } from "react-redux";
import { getSpecialWagerTooltipDescription } from "@urp/special-wagers/src/redux/selectors";
import { TooltipTag } from "@urp/special-wagers/src/components/TooltipTag";
import { Container, InnerContainer, TagContainer } from "./styled-components";
import { Props } from "./types";
import {
  CONTAINER,
  RESULTED,
  SPECIAL_WAGER,
  SUMMARY,
  TAG,
  TRACK
} from "../../utils/constants";
import FavoriteIcon from "../FavoriteIcon";

const CollapsibleSummary = ({
  hasPromo,
  module,
  qaLabel = "",
  track,
  isResults
}: Props) => {
  const { code: trackCode, name: trackName, isFinished: isResulted } = track;
  const { isFavorite: getIsFavorite, toggleFavorite } =
    useFavoriteWithRedux(module);
  const isMobile = useMediaQuery(breakpoints.tablet.max.sm);
  const isFavorite = getIsFavorite(trackCode);
  const specialWagerTooltipDescription = useSelector(
    getSpecialWagerTooltipDescription
  );
  const specialWagerType: SpecialWagerTypesNamesEnum =
    !isResults &&
    (isAdvancedSpecialPick(track.specialCardTypes ?? [])
      ? SpecialWagerTypesNamesEnum.NON_CONSECUTIVE_PICK
      : get(track, "specialCardTypes[0].specialCardTypeName", ""));
  const isSpecialPick =
    specialWagerType === SpecialWagerTypesNamesEnum.NON_CONSECUTIVE_PICK;
  const specialWagerTagLabel = isSpecialPick
    ? getSpecialPickLabel(track as SpecialWagerTrack)
    : specialWagerType;

  const tooltipDescription = getSpecialWagerTooltip(
    specialWagerTooltipDescription,
    specialWagerType,
    {
      betTypeName:
        track.numberOfRaces === 2
          ? `Daily Double`
          : `Pick ${track.numberOfRaces}`,
      raceAmount: track.numberOfRaces
    }
  );

  return (
    <Container
      data-qa-label={buildQaLabel([qaLabel, SUMMARY, CONTAINER, trackName])}
    >
      <InnerContainer>
        <FavoriteIcon
          isFavorite={isFavorite}
          toggleFavorite={() => toggleFavorite(trackCode, trackName)}
          trackName={trackName}
        />
        <Paragraph
          qaLabel={buildQaLabel([qaLabel, SUMMARY, TRACK, trackName])}
          fontFamily="bold"
        >
          {isMobile && specialWagerType && track
            ? getSpecialPickTrackName(track)
            : trackName}
        </Paragraph>
      </InnerContainer>
      <TagContainer>
        {(isResulted && (
          <Paragraph
            qaLabel={buildQaLabel([qaLabel, SUMMARY, RESULTED, trackName])}
          >
            All resulted
          </Paragraph>
        )) ||
          (hasPromo && (
            <Tag
              variant="alert"
              label={isMobile && specialWagerType ? "" : "promo"}
              size="medium"
              iconName="promo"
            />
          ))}
        {!!specialWagerType && (
          <TooltipTag
            qaLabel={buildQaLabel([
              qaLabel,
              SUMMARY,
              TRACK,
              trackName,
              SPECIAL_WAGER,
              TAG
            ])}
            label={specialWagerTagLabel}
            description={tooltipDescription}
          />
        )}
      </TagContainer>
    </Container>
  );
};

export default memo(CollapsibleSummary);
